<script setup lang="ts">
/**
 * Step 3 — Nominee Directors/Shareholders. Nominee exemption declaration +
 * nominator details. Mirrors test_incorporation.html step 5.
 */
import { ref, watch } from 'vue';
import SgIdTypeField from '@/features/setupEntity/components/sg/shared/SgIdTypeField.vue';
import SgB04NationalityField from '@/features/setupEntity/components/sg/shared/SgB04NationalityField.vue';
import SgJurisdictionDateField from '@/features/setupEntity/components/sg/shared/SgJurisdictionDateField.vue';
import SgMobilePhoneFields from '@/features/setupEntity/components/sg/shared/SgMobilePhoneFields.vue';
import SgAddressFields from '@/features/setupEntity/components/sg/shared/SgAddressFields.vue';
import CmBaseCodeAutocomplete from '../../shared/CmBaseCodeAutocomplete.vue';
import {
  createEmptyNominator,
  type EfNominator,
  type EfPositionHolder,
} from '../composables/incorporationEfilingModel';

const props = defineProps<{
  nominators: EfNominator[];
  nomineeExempt: { exempted: boolean; categories: string[] };
  holders: EfPositionHolder[];
  readonly?: boolean;
  /** 밸리데이션 포커스: 이 인덱스의 nominator 아코디언을 강제로 연다. */
  openIndex?: number | null;
}>();

const openPanels = ref<number[]>([]);
watch(
  () => props.openIndex,
  (idx) => {
    if (typeof idx === 'number' && idx >= 0 && !openPanels.value.includes(idx)) {
      openPanels.value = [...openPanels.value, idx];
    }
  },
);

// bizfile 정식 문구. 노미니 면제 카테고리 코드 4→(a) … 9→(f).
const EXEMPT_CATEGORIES = [
  {
    value: '4',
    label:
      '(a) Entity is a public company which shares are listed for quotation on an approved exchange in Singapore',
  },
  { value: '5', label: '(b) Entity is a company that is a Singapore financial institution' },
  { value: '6', label: '(c) Entity is a company that is wholly owned by the Government' },
  {
    value: '7',
    label:
      '(d) Entity is a company that is wholly owned by a statutory body established by or under a public Act for a public purpose',
  },
  {
    value: '8',
    label:
      '(e) Entity is a company that is a wholly owned subsidiary of a company mentioned in sub-paragraph (a), (b), (c), or (d)',
  },
  {
    value: '9',
    label:
      '(f) Entity is a company which shares are listed on a securities exchange in a country or territory outside Singapore and which is subject to (i) regulatory disclosure requirements; and (ii) requirements relating to adequate transparency in respect of its beneficial owners (imposed through stock exchange rules, law or other enforceable means)',
  },
];


function holderLabel(idx: number): string {
  const h = props.holders[idx];
  return h ? `${h.name || '(no name)'} — ${h.positions.join(', ')}` : `#${idx}`;
}

const nomineeOptions = () =>
  props.holders.map((h, i) => ({ title: holderLabel(i), value: i }));

function addNominator() {
  props.nominators.push(createEmptyNominator('director', props.holders.length ? 0 : -1));
}
function removeNominator(i: number) {
  props.nominators.splice(i, 1);
}
</script>

<template>
  <div class="ef-step">
    <div class="ef-step__section">
      <div class="ef-step__h">Nominee exemption</div>
      <v-radio-group
        v-model="nomineeExempt.exempted"
        inline
        density="compact"
        hide-details
        :disabled="readonly"
      >
        <v-radio label="No (enter nominators)" :value="false" />
        <v-radio label="Yes (exempted)" :value="true" />
      </v-radio-group>

      <div v-if="nomineeExempt.exempted" class="mt-2">
        <div class="ef-step__sub">Category of exemption</div>
        <v-checkbox
          v-for="c in EXEMPT_CATEGORIES"
          :key="c.value"
          v-model="nomineeExempt.categories"
          :label="c.label"
          :value="c.value"
          density="compact"
          hide-details
          :disabled="readonly"
        />
      </div>
    </div>

    <div v-if="!nomineeExempt.exempted" class="ef-step__section">
      <div class="ef-step__h">Nominators</div>
      <div v-if="!nominators.length" class="ef-step__empty">
        No nominators. Add one if a director or shareholder is a nominee.
      </div>

      <v-expansion-panels v-else v-model="openPanels" multiple variant="accordion" class="mb-3">
        <v-expansion-panel v-for="(n, i) in nominators" :key="i">
          <v-expansion-panel-title>
            <span class="nm-title">
              {{ n.nomineeKind === 'director' ? 'Nominee Director' : 'Nominee Shareholder' }} —
              {{ n.name || n.entityName || '(nominator)' }}
            </span>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="n.nomineeKind"
                  :items="[
                    { title: 'Nominee Director', value: 'director' },
                    { title: 'Nominee Shareholder', value: 'shareholder' },
                  ]"
                  label="Nominee kind"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :readonly="readonly"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="n.nomineeIndex"
                  :items="nomineeOptions()"
                  label="Nominee (position holder)"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :readonly="readonly"
                />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="n.nominatorCategory"
                  :items="[
                    { title: 'Individual', value: 'individual' },
                    { title: 'Corporate', value: 'corporate' },
                  ]"
                  label="Nominator category"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :readonly="readonly"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="n.addInfo"
                  label="Additional information"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :readonly="readonly"
                />
              </v-col>
            </v-row>

            <!-- individual nominator -->
            <template v-if="n.nominatorCategory === 'individual'">
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="n.name"
                    label="Name"
                    density="compact"
                    variant="outlined"
                    hide-details
                    :readonly="readonly"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="n.nameAlias"
                    label="Name alias"
                    density="compact"
                    variant="outlined"
                    hide-details
                    :readonly="readonly"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <SgIdTypeField v-model="n.idType" :readonly="readonly" />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="n.idNo"
                    label="ID No"
                    density="comfortable"
                    variant="outlined"
                    hide-details
                    :readonly="readonly"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <SgB04NationalityField v-model="n.nationality" :readonly="readonly" />
                </v-col>
                <v-col cols="12" sm="6">
                  <SgJurisdictionDateField v-model="n.birthDate" label="Birth date" :readonly="readonly" />
                </v-col>
              </v-row>
            </template>

            <!-- corporate nominator -->
            <template v-else>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="n.registeredLocally"
                    :items="[
                      { title: 'Yes (local SG entity)', value: 'YES' },
                      { title: 'No (foreign entity)', value: 'NO' },
                    ]"
                    label="Registered locally?"
                    density="compact"
                    variant="outlined"
                    hide-details
                    :readonly="readonly"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <CmBaseCodeAutocomplete
                    v-model="n.country"
                    main-code="B15"
                    label="Country of incorporation"
                    :read-only="readonly"
                  />
                </v-col>
                <v-col v-if="n.registeredLocally === 'YES'" cols="12" sm="6">
                  <v-text-field
                    v-model="n.uen"
                    label="Entity UEN"
                    density="compact"
                    variant="outlined"
                    hide-details
                    :readonly="readonly"
                  />
                </v-col>
                <template v-else>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="n.fbrn"
                      label="Foreign business registration number"
                      density="compact"
                      variant="outlined"
                      hide-details
                      :readonly="readonly"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="n.entityName"
                      label="Nominator name (company)"
                      density="compact"
                      variant="outlined"
                      hide-details
                      :readonly="readonly"
                    />
                  </v-col>
                </template>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="n.legalForm"
                    label="Legal form"
                    density="compact"
                    variant="outlined"
                    hide-details
                    :readonly="readonly"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="n.statute"
                    label="Statute"
                    density="compact"
                    variant="outlined"
                    hide-details
                    :readonly="readonly"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="n.register"
                    label="Name of corporate entity register (NA if none)"
                    density="compact"
                    variant="outlined"
                    hide-details
                    :readonly="readonly"
                  />
                </v-col>
              </v-row>
            </template>

            <v-row dense class="mt-1">
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="n.email"
                  label="Email"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :readonly="readonly"
                />
              </v-col>
              <v-col cols="12">
                <SgMobilePhoneFields
                  v-model:country-code="n.countryCode"
                  v-model:area-code="n.areaCode"
                  v-model:mobile-no="n.contactNo"
                  v-model:area-code-not-applicable="n.noArea"
                  :readonly="readonly"
                  show-area-code-toggle
                />
              </v-col>
            </v-row>

            <div class="ef-step__sub">Address</div>
            <SgAddressFields
              :model-value="n.address"
              :readonly="readonly"
              show-level-unit-toggle
              @update:model-value="n.address = $event"
            />

            <div v-if="!readonly" class="nm-actions">
              <v-btn
                variant="text"
                color="error"
                size="small"
                class="text-none"
                @click="removeNominator(i)"
              >
                Remove
              </v-btn>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>

      <v-btn
        v-if="!readonly"
        variant="tonal"
        color="primary"
        size="small"
        class="text-none"
        prepend-icon="mdi-plus"
        @click="addNominator"
      >
        Add nominator
      </v-btn>
    </div>
  </div>
</template>

<style scoped>
.ef-step__section {
  margin-bottom: 18px;
}
.ef-step__h {
  font-size: 14px;
  font-weight: 700;
  color: #1a2336;
  margin-bottom: 8px;
}
.ef-step__sub {
  font-size: 12.5px;
  font-weight: 700;
  color: #42506a;
  margin: 10px 0 4px;
}
.ef-step__empty {
  font-size: 12.5px;
  color: #8a95a8;
  padding: 8px 0;
}
.nm-title {
  font-weight: 600;
  font-size: 13px;
}
.nm-mobile {
  display: flex;
  gap: 6px;
}
.nm-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}
</style>
